import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  GripVertical,
  Folder,
  FolderPlus,
  FileText,
  Move,
  Search,
  ChevronRight,
  ChevronDown,
  MoreVertical,
  Copy,
  Eye,
  EyeOff,
  Wand2,
  Check
} from 'lucide-react';
import { apiService } from '../services/api';
import { HierarchyNode, Collection, Category } from '../types';
import { cn } from '../utils/cn';
import toast from 'react-hot-toast';
import CategoryManager from './CategoryManager';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface HierarchyManagerProps {
  onClose?: () => void;
}

type EditMode = 'category' | null;
type EditAction = 'create' | 'edit';

export const HierarchyManager: React.FC<HierarchyManagerProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdminOrInstructor = user?.roles?.some((role: string) => 
    role === 'admin' || role === 'instructor'
  ) || false;
  const [hierarchy, setHierarchy] = useState<HierarchyNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [selectedNode, setSelectedNode] = useState<HierarchyNode | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [editMode, setEditMode] = useState<EditMode>(null);
  const [editAction, setEditAction] = useState<EditAction>('create');
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [tutorialToMove, setTutorialToMove] = useState<HierarchyNode | null>(null);
  const [draggedNode, setDraggedNode] = useState<HierarchyNode | null>(null);
  const [dragOverNode, setDragOverNode] = useState<string | null>(null);
  const [selectedTutorials, setSelectedTutorials] = useState<Set<string>>(new Set());
  const [showBulkMoveModal, setShowBulkMoveModal] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [deletingTutorialId, setDeletingTutorialId] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: '',
    color: '#667eea',
    collectionId: '',
    parentCategoryId: '',
    sortOrder: 0,
    isVisible: true
  });

  useEffect(() => {
    loadHierarchy();
  }, []);

  const loadHierarchy = async () => {
    try {
      setLoading(true);
      const response = await apiService.getHierarchyTree();
      if (response.success && response.data) {
        setHierarchy(response.data);
        // Auto-expand all nodes
        const allNodeIds = new Set<string>();
        const collectIds = (nodes: HierarchyNode[]) => {
          nodes.forEach(node => {
            allNodeIds.add(node.id);
            if (node.children) {
              collectIds(node.children);
            }
          });
        };
        collectIds(response.data);
        setExpandedNodes(allNodeIds);
      }
    } catch (error: any) {
      console.error('Failed to load hierarchy:', error);
      toast.error('Failed to load hierarchy');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (nodeId: string) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const handleCreate = (parentCategoryId?: string) => {
    setEditMode('category');
    setEditAction('create');
    setSelectedNode(null);
    setFormData({
      name: '',
      description: '',
      icon: '',
      color: '#10b981',
      collectionId: '',
      parentCategoryId: parentCategoryId || '',
      sortOrder: 0,
      isVisible: true
    });
  };

  const handleEdit = (node: HierarchyNode) => {
    if (node.type === 'tutorial' || node.type === 'collection') return;
    
    setEditMode('category');
    setEditAction('edit');
    setSelectedNode(node);
    
    const data = node.category;
    setFormData({
      name: node.name,
      description: data?.description || '',
      icon: data?.icon || '',
      color: data?.color || '#10b981',
      collectionId: '',
      parentCategoryId: data?.parentCategoryId || '',
      sortOrder: data?.sortOrder || 0,
      isVisible: data?.isVisible !== false
    });
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    try {
      if (editAction === 'create') {
        await apiService.createCategory({
          name: formData.name,
          description: formData.description,
          icon: formData.icon,
          color: formData.color,
          parentCategoryId: formData.parentCategoryId || undefined,
          sortOrder: formData.sortOrder
        });
        toast.success('Category created');
      } else {
        await apiService.updateCategory(selectedNode!.id, {
          name: formData.name,
          description: formData.description,
          icon: formData.icon,
          color: formData.color,
          parentCategoryId: formData.parentCategoryId || undefined,
          sortOrder: formData.sortOrder,
          isVisible: formData.isVisible
        });
        toast.success('Category updated');
      }

      setEditMode(null);
      setSelectedNode(null);
      await loadHierarchy();
    } catch (error: any) {
      console.error('Error saving:', error);
      toast.error(error.message || 'Failed to save');
    }
  };

  const handleDelete = async (node: HierarchyNode) => {
    if (node.type === 'tutorial') {
      toast.error('Cannot delete tutorials from here. Use tutorial management.');
      return;
    }

    const name = node.name;
    const isCollection = node.type === 'collection';
    const tutorialCount = node.tutorialCount || 0;
    
    let confirmMessage = '';
    if (isCollection) {
      confirmMessage = `Are you sure you want to delete collection "${name}"? This will remove the collection but categories and tutorials will remain. Note: Collections are deprecated - consider migrating to categories instead.`;
    } else {
      confirmMessage = `Are you sure you want to delete category "${name}"? This will also delete all subcategories${tutorialCount > 0 ? ` and move ${tutorialCount} tutorial${tutorialCount === 1 ? '' : 's'} to uncategorized` : ''}.`;
    }
    
    if (!confirm(confirmMessage)) {
      return;
    }

    try {
      if (isCollection) {
        await apiService.deleteCollection(node.id);
        toast.success('Collection deleted');
      } else {
        await apiService.deleteCategory(node.id);
        toast.success('Category deleted');
      }
      await loadHierarchy();
    } catch (error: any) {
      console.error('Error deleting:', error);
      toast.error(error.message || 'Failed to delete');
    }
  };

  const handleMoveTutorial = (tutorial: HierarchyNode) => {
    setTutorialToMove(tutorial);
    setShowMoveModal(true);
  };

  const confirmMoveTutorial = async (targetCategoryId?: string) => {
    if (!tutorialToMove) return;

    try {
      await apiService.moveTutorial(
        tutorialToMove.tutorial!.id,
        undefined, // No collectionId
        targetCategoryId
      );
      toast.success('Tutorial moved');
      setShowMoveModal(false);
      setTutorialToMove(null);
      await loadHierarchy();
    } catch (error: any) {
      console.error('Error moving tutorial:', error);
      toast.error(error.message || 'Failed to move tutorial');
    }
  };

  const handleDragStart = (e: React.DragEvent, node: HierarchyNode) => {
    setDraggedNode(node);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', node.id);
  };

  const handleDragOver = (e: React.DragEvent, nodeId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverNode(nodeId);
  };

  const handleDragLeave = () => {
    setDragOverNode(null);
  };

  const handleDrop = async (e: React.DragEvent, targetNode: HierarchyNode) => {
    e.preventDefault();
    setDragOverNode(null);

    if (!draggedNode || draggedNode.id === targetNode.id) {
      setDraggedNode(null);
      return;
    }

    // Don't allow dropping tutorials on other tutorials
    if (draggedNode.type === 'tutorial' && targetNode.type === 'tutorial') {
      setDraggedNode(null);
      return;
    }

    try {
      // Determine new location - only categories now
      let targetCategoryId: string | undefined;

      if (targetNode.type === 'category') {
        targetCategoryId = targetNode.id;
      } else if (targetNode.type === 'collection') {
        // Collections are treated as top-level categories, so no parent
        targetCategoryId = undefined;
      }

      // Move the node
      if (draggedNode.type === 'tutorial') {
        await apiService.moveTutorial(
          draggedNode.tutorial!.id,
          undefined, // No collectionId
          targetCategoryId
        );
        toast.success('Tutorial moved');
      } else if (draggedNode.type === 'category') {
        // For categories, update their parent
        const updates = [{
          id: draggedNode.id,
          type: 'category' as const,
          sortOrder: 0,
          categoryId: targetCategoryId,
          parentCategoryId: targetNode.type === 'category' ? targetNode.id : undefined
        }];
        await apiService.reorderHierarchy(updates);
        toast.success('Category moved');
      }

      await loadHierarchy();
    } catch (error: any) {
      console.error('Error moving:', error);
      toast.error(error.message || 'Failed to move');
    } finally {
      setDraggedNode(null);
    }
  };

  // Filter hierarchy based on search
  const filteredHierarchy = useMemo(() => {
    if (!searchQuery.trim()) return hierarchy;

    const query = searchQuery.toLowerCase();
    const filterNodes = (nodes: HierarchyNode[]): HierarchyNode[] => {
      return nodes
        .map(node => {
          const matches = node.name.toLowerCase().includes(query);
          const filteredChildren = node.children ? filterNodes(node.children) : [];

          if (matches || filteredChildren.length > 0) {
            return {
              ...node,
              children: filteredChildren.length > 0 ? filteredChildren : node.children
            };
          }
          return null;
        })
        .filter((node): node is HierarchyNode => node !== null);
    };

    return filterNodes(hierarchy);
  }, [hierarchy, searchQuery]);

  const renderNode = (node: HierarchyNode, depth: number = 0): React.ReactNode => {
    const isExpanded = expandedNodes.has(node.id);
    const hasChildren = node.children && node.children.length > 0;
    const isSelected = selectedNode?.id === node.id;

    return (
      <div key={node.id} className="select-none">
        <div
          draggable={node.type !== 'tutorial' || true}
          onDragStart={(e) => handleDragStart(e, node)}
          onDragOver={(e) => handleDragOver(e, node.id)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, node)}
          className={cn(
            "flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors group cursor-move",
            isSelected && "bg-blue-50 border border-blue-200",
            dragOverNode === node.id && "bg-blue-100 border-2 border-blue-400 border-dashed",
            draggedNode?.id === node.id && "opacity-50"
          )}
          style={{ paddingLeft: `${depth * 24 + 12}px` }}
        >
          {/* Expand/Collapse */}
          {hasChildren ? (
            <button
              onClick={() => toggleExpand(node.id)}
              className="p-1 hover:bg-gray-200 rounded"
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-gray-600" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-600" />
              )}
            </button>
          ) : (
            <span className="w-6" />
          )}

          {/* Move Button - Always visible on the left */}
          {node.type === 'tutorial' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleMoveTutorial(node);
              }}
              className="p-1.5 hover:bg-gray-200 rounded text-gray-600 flex-shrink-0"
              title="Move tutorial"
            >
              <Move className="w-4 h-4" />
            </button>
          )}

          {/* Icon */}
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{
              backgroundColor: node.collection?.color || node.category?.color
                ? `${(node.collection?.color || node.category?.color)}20`
                : node.type === 'collection' ? '#667eea20' : node.type === 'category' ? '#10b98120' : '#gray-200',
              color: node.collection?.color || node.category?.color || (node.type === 'collection' ? '#667eea' : '#10b981')
            }}
          >
            {node.type === 'collection' || node.type === 'category' ? (
              node.collection?.icon || node.category?.icon ? (
                <span className="text-lg">{node.collection?.icon || node.category?.icon}</span>
              ) : (
                <Folder className="w-4 h-4" />
              )
            ) : (
              <FileText className="w-4 h-4 text-gray-500" />
            )}
          </div>

          {/* Name and Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900 truncate">{node.name}</span>
              {node.tutorialCount !== undefined && node.tutorialCount > 0 && (
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                  {node.tutorialCount} {node.tutorialCount === 1 ? 'tutorial' : 'tutorials'}
                </span>
              )}
            </div>
            {(node.collection?.description || node.category?.description) && (
              <p className="text-xs text-gray-500 truncate">
                {node.collection?.description || node.category?.description}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {node.type === 'tutorial' ? (
              <>
                {isAdminOrInstructor && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/builder/ai-assisted/${node.tutorial!.id}`);
                      }}
                      className="p-1.5 hover:bg-purple-100 rounded text-purple-600"
                      title="Edit with AI"
                    >
                      <Wand2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/builder/${node.tutorial!.id}`);
                      }}
                      className="p-1.5 hover:bg-blue-100 rounded text-blue-600"
                      title="Manual Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!confirm(`Are you sure you want to delete "${node.tutorial!.title}"? This action cannot be undone.`)) {
                          return;
                        }
                        setDeletingTutorialId(node.tutorial!.id);
                        try {
                          const response = await apiService.deleteTutorial(node.tutorial!.id);
                          if (response.success) {
                            toast.success('Tutorial deleted successfully');
                            await loadHierarchy();
                          } else {
                            toast.error('Failed to delete tutorial');
                          }
                        } catch (error: any) {
                          console.error('Delete tutorial error:', error);
                          toast.error(error.message || 'Failed to delete tutorial');
                        } finally {
                          setDeletingTutorialId(null);
                        }
                      }}
                      disabled={deletingTutorialId === node.tutorial!.id}
                      className="p-1.5 hover:bg-red-100 rounded text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Delete Tutorial"
                    >
                      {deletingTutorialId === node.tutorial!.id ? (
                        <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  </>
                )}
              </>
            ) : (
              <>
                {node.type === 'category' && (
                  <>
                    <button
                      onClick={() => handleEdit(node)}
                      className="p-1.5 hover:bg-blue-100 rounded text-blue-600"
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleCreate(node.id)}
                      className="p-1.5 hover:bg-green-100 rounded text-green-600"
                      title="Add subcategory"
                    >
                      <FolderPlus className="w-4 h-4" />
                    </button>
                  </>
                )}
                <button
                  onClick={() => handleDelete(node)}
                  className="p-1.5 hover:bg-red-100 rounded text-red-600"
                  title={node.type === 'collection' ? 'Delete collection (deprecated)' : 'Delete'}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </>
            )}
          </div>

          {/* Selection checkbox for tutorials - Far right */}
          {node.type === 'tutorial' && (
            <div className="relative flex-shrink-0">
              <input
                type="checkbox"
                checked={selectedTutorials.has(node.id)}
                onChange={(e) => {
                  const newSelected = new Set(selectedTutorials);
                  if (e.target.checked) {
                    newSelected.add(node.id);
                  } else {
                    newSelected.delete(node.id);
                  }
                  setSelectedTutorials(newSelected);
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-4 h-4 rounded border-gray-300 text-[#667eea] focus:ring-[#667eea] checked:bg-[#667eea] checked:border-[#667eea] appearance-none relative"
              />
              {selectedTutorials.has(node.id) && (
                <Check className="absolute top-0 left-0 w-4 h-4 text-white pointer-events-none" strokeWidth={3} />
              )}
            </div>
          )}
        </div>

        {/* Children */}
        {hasChildren && isExpanded && (
          <div className="mt-1">
            {node.children!.map(child => renderNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-200 rounded-xl mx-auto mb-4 animate-pulse" />
          <p className="text-gray-600">Loading hierarchy...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-white border-b p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Hierarchy Management</h1>
            <p className="text-sm text-gray-600 mt-1">Organize categories and tutorials</p>
          </div>
          <div className="flex items-center gap-2">
            {selectedTutorials.size > 0 && (
              <button
                onClick={() => setShowBulkMoveModal(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
              >
                <Move className="w-4 h-4" />
                Move {selectedTutorials.size} Tutorial{selectedTutorials.size !== 1 ? 's' : ''}
              </button>
            )}
            <button
              onClick={() => setShowCategoryManager(true)}
              className="px-4 py-2 bg-[#10b981] text-white rounded-lg hover:bg-[#0d9d6e] transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Manage Categories
            </button>
            {onClose && (
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search collections, categories, or tutorials..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#667eea] focus:border-transparent"
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-4xl mx-auto">
          {filteredHierarchy.length === 0 ? (
            <div className="text-center py-12">
              <Folder className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">No categories found</p>
              <button
                onClick={() => handleCreate()}
                className="px-4 py-2 bg-[#667eea] text-white rounded-lg hover:bg-[#5a6fd8] transition-colors"
              >
                Create First Category
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              {filteredHierarchy.map(node => renderNode(node))}
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      <AnimatePresence>
        {editMode && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setEditMode(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">
                  {editAction === 'create' ? 'Create' : 'Edit'} Category
                </h2>
                <button
                  onClick={() => setEditMode(null)}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#667eea] focus:border-transparent"
                    placeholder="Enter name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#667eea] focus:border-transparent"
                    rows={3}
                    placeholder="Enter description"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Icon
                    </label>
                    <input
                      type="text"
                      value={formData.icon}
                      onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#667eea] focus:border-transparent"
                      placeholder="📁"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Color
                    </label>
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      className="w-full h-10 border border-gray-300 rounded-lg cursor-pointer"
                    />
                  </div>
                </div>

                {editMode === 'category' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Collection
                    </label>
                    <select
                      value={formData.collectionId}
                      onChange={(e) => setFormData({ ...formData, collectionId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#667eea] focus:border-transparent"
                    >
                      <option value="">Select collection...</option>
                      {hierarchy.map(node => (
                        node.type === 'collection' && (
                          <option key={node.id} value={node.id}>
                            {node.name}
                          </option>
                        )
                      ))}
                    </select>
                  </div>
                )}

                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="isVisible"
                      checked={formData.isVisible}
                      onChange={(e) => setFormData({ ...formData, isVisible: e.target.checked })}
                      className="rounded border-gray-300 text-[#667eea] focus:ring-[#667eea]"
                    />
                    <label htmlFor="isVisible" className="text-sm text-gray-700">
                      Visible
                    </label>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <button
                    onClick={() => setEditMode(null)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 bg-[#667eea] text-white rounded-lg hover:bg-[#5a6fd8] transition-colors flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    Save
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bulk Move Modal */}
      <AnimatePresence>
        {showBulkMoveModal && selectedTutorials.size > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setShowBulkMoveModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl"
            >
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Move {selectedTutorials.size} Tutorial{selectedTutorials.size !== 1 ? 's' : ''}
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    To Category
                  </label>
                  <select
                    id="bulkMoveCategory"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#667eea] focus:border-transparent"
                    defaultValue=""
                  >
                    <option value="">Top-level (no category)</option>
                    {(() => {
                      const collectCategories = (nodes: HierarchyNode[], prefix = ''): Array<{id: string, name: string}> => {
                        const result: Array<{id: string, name: string}> = [];
                        nodes.forEach(node => {
                          if (node.type === 'category') {
                            result.push({ id: node.id, name: prefix + node.name });
                            if (node.children) {
                              const subcats = collectCategories(node.children, prefix + '  ');
                              result.push(...subcats);
                            }
                          }
                        });
                        return result;
                      };
                      const allCategories = collectCategories(hierarchy);
                      return allCategories.map(cat => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ));
                    })()}
                  </select>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <button
                    onClick={() => setShowBulkMoveModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      const categorySelect = document.getElementById('bulkMoveCategory') as HTMLSelectElement;
                      const targetCategoryId = categorySelect.value || undefined;

                      try {
                        let successCount = 0;
                        let failCount = 0;

                        for (const tutorialId of selectedTutorials) {
                          try {
                            // Find the tutorial node to get its actual tutorial ID
                            const findTutorial = (nodes: HierarchyNode[]): HierarchyNode | null => {
                              for (const node of nodes) {
                                if (node.id === tutorialId && node.type === 'tutorial') {
                                  return node;
                                }
                                if (node.children) {
                                  const found = findTutorial(node.children);
                                  if (found) return found;
                                }
                              }
                              return null;
                            };

                            const tutorialNode = findTutorial(hierarchy);
                            if (tutorialNode?.tutorial) {
                              await apiService.moveTutorial(
                                tutorialNode.tutorial.id,
                                undefined, // No collectionId
                                targetCategoryId
                              );
                              successCount++;
                            }
                          } catch (error) {
                            failCount++;
                          }
                        }

                        if (successCount > 0) {
                          toast.success(`Moved ${successCount} tutorial${successCount !== 1 ? 's' : ''}`);
                        }
                        if (failCount > 0) {
                          toast.error(`Failed to move ${failCount} tutorial${failCount !== 1 ? 's' : ''}`);
                        }

                        setShowBulkMoveModal(false);
                        setSelectedTutorials(new Set());
                        await loadHierarchy();
                      } catch (error: any) {
                        console.error('Error moving tutorials:', error);
                        toast.error(error.message || 'Failed to move tutorials');
                      }
                    }}
                    className="px-4 py-2 bg-[#667eea] text-white rounded-lg hover:bg-[#5a6fd8] transition-colors flex items-center gap-2"
                  >
                    <Move className="w-4 h-4" />
                    Move {selectedTutorials.size} Tutorial{selectedTutorials.size !== 1 ? 's' : ''}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Move Tutorial Modal */}
      <AnimatePresence>
        {showMoveModal && tutorialToMove && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setShowMoveModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl"
            >
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Move Tutorial: {tutorialToMove.name}
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    To Category
                  </label>
                  <select
                    id="moveCategory"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#667eea] focus:border-transparent"
                    defaultValue=""
                  >
                    <option value="">Top-level (no category)</option>
                    {(() => {
                      const collectCategories = (nodes: HierarchyNode[], prefix = ''): Array<{id: string, name: string}> => {
                        const result: Array<{id: string, name: string}> = [];
                        nodes.forEach(node => {
                          if (node.type === 'category') {
                            result.push({ id: node.id, name: prefix + node.name });
                            if (node.children) {
                              const subcats = collectCategories(node.children, prefix + '  ');
                              result.push(...subcats);
                            }
                          }
                        });
                        return result;
                      };
                      const allCategories = collectCategories(hierarchy);
                      return allCategories.map(cat => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ));
                    })()}
                  </select>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <button
                    onClick={() => setShowMoveModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      const categorySelect = document.getElementById('moveCategory') as HTMLSelectElement;
                      confirmMoveTutorial(
                        categorySelect.value || undefined
                      );
                    }}
                    className="px-4 py-2 bg-[#667eea] text-white rounded-lg hover:bg-[#5a6fd8] transition-colors flex items-center gap-2"
                  >
                    <Move className="w-4 h-4" />
                    Move
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Category Manager Modal */}
      {showCategoryManager && (
        <CategoryManager
          onClose={() => {
            setShowCategoryManager(false);
            loadHierarchy(); // Reload hierarchy after category management
          }}
          isModal={true}
        />
      )}
    </div>
  );
};

export default HierarchyManager;

